/*
 * Longeva Aesthetics — cart-drawer cross-sell (#43, research-led).
 *
 * Upgrade of the #38 strip. Two studies (Rebuy / Candy Rack /
 * Selleasy / Shopify Search & Discovery / Baymard) drove these rules:
 *
 * RELEVANCE — Shopify /recommendations intent=related returns
 * `collection_fallback` junk for low-signal SKUs (acetone -> filler).
 * We never show that: filter the related set to items that share the
 * cart product's product_type OR a tag. If nothing qualifies, render
 * NOTHING (an empty slot beats an irrelevant one for a clinical
 * brand). No paid app, no Storefront token needed.
 *
 * IN-DRAWER ADD — quantity stepper + Add on single-variant products
 * only (multi-variant link to the PDP, never a broken in-drawer
 * picker). Add = /cart/add.js then dispatch Horizon's `cart:update`
 * so Horizon re-renders its own drawer (no manual HTML injection).
 *
 * SAFETY — read-mostly, fully try/catch guarded, idempotent, no
 * global overrides, isAdding lock; a failure can never block or
 * break the cart / checkout.
 */
(function () {
  'use strict';
  return; // Disabled — cross-sell removed per Anthony instruction

  var INJECTED = 'longeva-cart-recs';
  var inFlight = false;
  var isAdding = false;

  function jget(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function drawerContent() {
    return (
      document.querySelector('.cart-drawer__content') ||
      document.querySelector('cart-items-component[data-drawer] .cart-drawer__content') ||
      document.querySelector('cart-items-component') ||
      null
);
  }

  function alreadyInjected(c) {
    return !!c.querySelector('.' + INJECTED);
  }

  function money(cents) {
    try { return '£' + ((Number(cents) || 0) / 100).toFixed(2); }
    catch (e) { return ''; }
  }

  function sizedImage(src) {
    if (!src) return '';
    try {
      if (src.indexOf('//') === 0) src = 'https:' + src;
      if (/cdn\.shopify|\/shop\/files|\/products\//.test(src) && src.indexOf('width=') === -1) {
        src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=240';
      }
    } catch (e) {}
    return src;
  }

  function lc(v) { return String(v == null ? '' : v).trim().toLowerCase(); }

  // The customer's journey context: product types + tags from what
  // they actually browsed (recently-viewed, recorded on the PDP) AND
  // what is in the cart. Recommendations are matched against this whole
  // journey, not just the first cart line.
  function readViewed() {
    try {
      var v = JSON.parse(localStorage.getItem('lv_viewed') || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }

  function gatherUniverse(cart, viewed) {
    var types = {}, tags = {};
    function add(t, tg) {
      if (t) types[lc(t)] = 1;
      (tg || []).forEach(function (x) { var k = lc(x); if (k) tags[k] = 1; });
    }
    viewed.forEach(function (v) { if (v) add(v.type, v.tags); });
    var handles = [];
    ((cart && cart.items) || []).forEach(function (i) {
      if (i.handle && handles.indexOf(i.handle) === -1 && handles.length < 4) {
        handles.push(i.handle);
      }
    });
    return Promise.all(
      handles.map(function (h) { return jget('/products/' + h + '.js'); })
).then(function (prods) {
      prods.forEach(function (p) {
        if (p) add(p.type || p.product_type, p.tags);
      });
      return { types: Object.keys(types), tags: Object.keys(tags) };
    });
  }

  // Keep only recs genuinely relevant to the customer's journey
  // (shared product_type OR tag). Excludes anything already in cart and
  // the seed itself. If nothing qualifies the caller renders NOTHING
  // (no heading) — an empty slot beats a misleading one.
  function filterRelevant(recs, uniTypes, uniTags, excludeIds) {
    return recs.filter(function (p) {
      if (!p || !p.id || excludeIds.indexOf(p.id) !== -1) return false;
      var t = lc(p.type);
      var typeHit = t && uniTypes.indexOf(t) !== -1;
      var tagHit = (p.tags || []).some(function (x) {
        return uniTags.indexOf(lc(x)) !== -1;
      });
      return typeHit || tagHit;
    });
  }

  // A product is directly addable only if it has a single sellable
  // variant (no in-drawer variant picker, per research).
  function addableVariant(p) {
    try {
      var vs = p.variants || [];
      if (vs.length === 1 && vs[0] && vs[0].available !== false) return vs[0];
      if (
        vs.length > 1 &&
        vs[0] &&
        lc(vs[0].title) === 'default title' &&
        vs[0].available !== false
) {
        return vs[0];
      }
    } catch (e) {}
    return null;
  }

  function addToCart(variantId, qty, btn, defaultLabel) {
    if (isAdding) return;
    isAdding = true;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function () {
        // Let Horizon re-render its own drawer (no manual injection).
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { itemCount: qty, source: 'product-form-component' } }
          })
);
        btn.textContent = 'Added';
        btn.classList.add(INJECTED + '__add--done');
        setTimeout(function () {
          btn.textContent = defaultLabel;
          btn.classList.remove(INJECTED + '__add--done');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function () {
        btn.textContent = 'Try again';
        btn.disabled = false;
      })
      .finally(function () {
        isAdding = false;
      });
  }

  function buildCard(p) {
    var variant = addableVariant(p);
    var card = document.createElement(variant ? 'div' : 'a');
    card.className = INJECTED + '__card';
    if (!variant) {
      card.href = p.url;
      card.setAttribute('rel', 'nofollow');
    }

    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && typeof img === 'object') img = img.src || img.url || '';
    if (img) {
      var fig = document.createElement(variant ? 'a' : 'span');
      fig.className = INJECTED + '__media';
      if (variant) { fig.href = p.url; fig.setAttribute('rel', 'nofollow'); }
      var im = document.createElement('img');
      im.src = sizedImage(img);
      im.alt = '';
      im.loading = 'lazy';
      im.decoding = 'async';
      fig.appendChild(im);
      card.appendChild(fig);
    }

    var meta = document.createElement(variant ? 'div' : 'span');
    meta.className = INJECTED + '__meta';
    var nameEl = document.createElement(variant ? 'a' : 'span');
    nameEl.className = INJECTED + '__name';
    nameEl.textContent = String(p.title || '').trim().slice(0, 70);
    if (variant) { nameEl.href = p.url; nameEl.setAttribute('rel', 'nofollow'); }
    var priceEl = document.createElement('span');
    priceEl.className = INJECTED + '__price';
    priceEl.textContent = money(p.price);
    meta.appendChild(nameEl);
    meta.appendChild(priceEl);
    card.appendChild(meta);

    if (variant) {
      var actions = document.createElement('div');
      actions.className = INJECTED + '__actions';

      var stepper = document.createElement('div');
      stepper.className = INJECTED + '__qty';
      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = INJECTED + '__qty-btn';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.textContent = '−';
      var val = document.createElement('span');
      val.className = INJECTED + '__qty-val';
      val.textContent = '1';
      val.setAttribute('aria-live', 'polite');
      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = INJECTED + '__qty-btn';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.textContent = '+';
      var qty = 1;
      minus.addEventListener('click', function () {
        if (qty > 1) { qty--; val.textContent = String(qty); }
      });
      plus.addEventListener('click', function () {
        if (qty < 10) { qty++; val.textContent = String(qty); }
      });
      stepper.appendChild(minus);
      stepper.appendChild(val);
      stepper.appendChild(plus);

      var add = document.createElement('button');
      add.type = 'button';
      add.className = INJECTED + '__add';
      add.textContent = 'Add';
      add.addEventListener('click', function () {
        addToCart(variant.id, qty, add, 'Add');
      });

      actions.appendChild(stepper);
      actions.appendChild(add);
      card.appendChild(actions);
    }

    return card;
  }

  function buildStrip(products) {
    var wrap = document.createElement('div');
    wrap.className = INJECTED;
    wrap.setAttribute('aria-label', 'Often paired with');
    var h = document.createElement('p');
    h.className = INJECTED + '__title';
    h.textContent = 'Often paired with';
    wrap.appendChild(h);
    var rail = document.createElement('div');
    rail.className = INJECTED + '__rail';
    products.slice(0, 3).forEach(function (p) {
      rail.appendChild(buildCard(p));
    });
    wrap.appendChild(rail);
    return wrap;
  }

  function inject() {
    if (inFlight) return;
    var c = drawerContent();
    if (!c || alreadyInjected(c)) return;
    inFlight = true;

    var viewed = readViewed();
    jget('/cart.js')
      .then(function (cart) {
        cart = cart || { items: [] };
        // "Often paired with" is a cart device. With an empty cart there
        // is nothing to pair with, so render nothing (#116) — the
        // journey signal only enriches relevance for a non-empty cart.
        if (!cart.items || !cart.items.length) { inFlight = false; return; }
        var cartIds = (cart.items || []).map(function (i) { return i.product_id; });
        // Seed = the product the customer browsed but has NOT added
        // (strongest intent signal); else the first cart line.
        var viewedNotInCart = viewed.filter(function (v) {
          return v && v.id && cartIds.indexOf(v.id) === -1;
        });
        var seedId =
          (viewedNotInCart[0] && viewedNotInCart[0].id) ||
          (cart.items && cart.items[0] && cart.items[0].product_id) ||
          null;
        if (!seedId) { inFlight = false; return; }
        var excludeIds = cartIds.concat([seedId]);
        return gatherUniverse(cart, viewed).then(function (uni) {
          return jget(
            '/recommendations/products.json?product_id=' +
              encodeURIComponent(seedId) +
              '&limit=12&intent=related'
).then(function (json) {
            var recs = (json && json.products) || [];
            var relevant = filterRelevant(recs, uni.types, uni.tags, excludeIds);
            if (!relevant.length) return; // empty > misleading: no heading
            var cont = drawerContent();
            if (!cont || alreadyInjected(cont)) return;
            var strip = buildStrip(relevant);
            // Premium-app pattern (Rebuy / Candy Rack): the upsell
            // lives INSIDE Horizon's scrollable cart-line body, above
            // the sticky checkout footer — never as a third flex child
            // of .cart-drawer__content. Horizon's flex column expects
            // exactly two children (the shrinkable .cart-drawer__items
            // scroll region + a sticky summary); a third non-shrinkable
            // child starves the items region on short viewports and
            // clips the cart line's quantity controls (#43 short-screen
            // overlap). Injecting into the scroll body restores that
            // model and lets the strip scroll with the cart lines.
            var scrollBody = cont.querySelector('.cart-drawer__items');
            if (scrollBody) {
              scrollBody.appendChild(strip);
            } else {
              var anchor = cont.querySelector(
                '[class*="cart-drawer__footer"], [class*="cart__footer"], ' +
                  '[class*="cart-totals"], [class*="cart-summary"], ' +
                  '[class*="summary"], [class*="footer"]'
);
              if (anchor && anchor.parentNode) {
                anchor.parentNode.insertBefore(strip, anchor);
              } else {
                cont.appendChild(strip);
              }
            }
          });
        });
      })
      .catch(function () {})
      .finally(function () { inFlight = false; });
  }

  function cartDrawerVisible() {
    var c = drawerContent();
    if (!c) return false;
    var r = c.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function schedule() {
    try {
      if (cartDrawerVisible()) window.requestAnimationFrame(inject);
    } catch (e) {}
  }

  try {
    var mo = new MutationObserver(function () { schedule(); });
    mo.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open', 'data-drawer', 'class']
    });
    document.addEventListener('cart:refresh', schedule, { passive: true });
    document.addEventListener('cart:updated', schedule, { passive: true });
    document.addEventListener('cart:update', schedule, { passive: true });
    if (document.readyState !== 'loading') schedule();
    else document.addEventListener('DOMContentLoaded', schedule);
  } catch (e) {}
})();
